import {Badge} from "@/components/ui/badge.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {Input} from "@/components/ui/input.tsx";
import {annotationKinds} from "@/dungeons/annotations/AnnotationAppearance.ts";
import type {LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import DoorInspector from "@/dungeons/editor/geometry/DoorInspector.tsx";
import TransitionInspector from "@/dungeons/editor/geometry/TransitionInspector.tsx";
import {validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import type {DungeonFormOutput} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import type {GeometryEditorController} from "@/dungeons/editor/geometry/useGeometryEditorController.ts";
import type useGridCalibration from "@/dungeons/editor/grid/useGridCalibration.ts";
import type {DungeonAnnotationsController} from "@/dungeons/editor/annotations/useDungeonAnnotations.ts";
import type {DungeonLightsController} from "@/dungeons/editor/lighting/useDungeonLights.ts";
import LightInspector from "@/dungeons/editor/lighting/LightInspector.tsx";
import {LIGHT_PRESET_LABELS, LIGHT_PRESETS} from "@/dungeons/lighting/LightPresets.ts";
import {editorToolLabels, type UnifiedEditorTool} from "./UnifiedEditorTools.ts";
import {Check, RotateCcw, Trash2} from "lucide-react";
import DirectionalLightInspector from "@/dungeons/lighting/DirectionalLightInspector.tsx";
import type useLevelLighting from "@/dungeons/editor/lighting/useLevelLighting.ts";

type GridController = ReturnType<typeof useGridCalibration>;

export default function UnifiedEditorSidebar({tool, document, sourceLevelId, levels, hasImage, grid, geometry, annotations, lights, levelLighting,
    snappingEnabled, onSnappingEnabledChange, onCalibrationApplied, onFinishTransition}: {
    tool: UnifiedEditorTool;
    document: LevelGeometryDocument;
    sourceLevelId: string;
    levels: DungeonFormOutput["levels"];
    hasImage: boolean;
    grid: GridController;
    geometry: GeometryEditorController;
    annotations: DungeonAnnotationsController;
    lights: DungeonLightsController;
    levelLighting: ReturnType<typeof useLevelLighting>;
    snappingEnabled: boolean;
    onSnappingEnabledChange: (enabled: boolean) => void;
    onCalibrationApplied: () => void;
    onFinishTransition: () => void;
}) {
    const {state, selectedWall, selectedDoor, selectedTransition, commands} = geometry;
    const hasGeometry = document.walls.length + document.doors.length + document.transitions.length +
        document.annotations.length + document.lights.length > 0;
    return <>
        <h2 className="text-sm font-semibold">{editorToolLabels[tool]}</h2>
        {!document.registration.calibrated && <Badge variant="outline">Grid noch nicht kalibriert</Badge>}
        {tool === "calibrate" && <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Wähle zwei Grid-Schnittpunkte auf derselben waagerechten oder
                senkrechten Linie.</p>
            <label className="block space-y-1">Zellen zwischen den Punkten
                <Input type="number" min={1} step={1} value={grid.cellCount}
                       onChange={event => grid.setCellCount(Number(event.target.value))}/>
            </label>
            <label className="block space-y-1">Meter pro Zelle
                <Input type="number" min={0.01} step={0.1} value={grid.metersPerCell}
                       onChange={event => grid.setMetersPerCell(Number(event.target.value))}/>
            </label>
            {grid.calibrationPoints.length === 2 && <p>{grid.pixelsPerCell.toFixed(2)} Pixel pro Zelle</p>}
            {document.registration.calibrated && hasGeometry &&
                <p className="rounded border border-warning/50 p-2 text-warning">
                    Erneute Kalibrierung transformiert vorhandene Wände, Türen, Übergänge, Annotationen und Lichter.
                </p>}
            <div className="flex gap-2">
                <Button type="button" size="sm" disabled={!hasImage || !grid.canApplyCalibration}
                        onClick={() => { if (grid.applyCalibration()) onCalibrationApplied(); }}>
                    Übernehmen
                </Button>
                <Button type="button" size="icon-sm" variant="outline" title="Messpunkte zurücksetzen"
                        onClick={() => grid.setCalibrationPoints([])}><RotateCcw/></Button>
            </div>
        </div>}
        {tool === "origin" && <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Wähle denselben Grid-Schnittpunkt auf mehreren Karten als Ursprung (0, 0).</p>
            {grid.originPoint && <p>Bildpunkt: {grid.originPoint.x.toFixed(1)} / {grid.originPoint.y.toFixed(1)} px</p>}
            <div className="flex gap-2">
                <Button type="button" size="sm" disabled={!grid.originPoint} onClick={grid.applyOrigin}>Übernehmen</Button>
                <Button type="button" size="icon-sm" variant="outline" title="Ursprungspunkt zurücksetzen"
                        onClick={() => grid.setOriginPoint(undefined)}><RotateCcw/></Button>
            </div>
        </div>}
        {(tool === "wall" || tool === "door" || tool === "transition" || tool === "select") && <>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox checked={snappingEnabled}
                          onCheckedChange={checked => onSnappingEnabledChange(checked === true)}/>
                Einrasten aktiv
            </label>
            {tool === "wall" && <div className="space-y-3 text-sm">
                <p className="text-muted-foreground">Linksklick setzt einen Punkt, Doppelklick schließt die Wand ab.
                    Rechtsklick verwirft den letzten Punkt; Strg deaktiviert das Einrasten.</p>
                <p>{state.wallDraft.length} Wandpunkte</p>
                <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={state.wallDraft.length < 2} onClick={commands.finishWall}>
                        <Check/>Wand abschließen
                    </Button>
                    <Button type="button" size="icon-sm" variant="outline" disabled={state.wallDraft.length === 0}
                            title="Wandentwurf verwerfen" onClick={commands.clearWallDraft}><Trash2/></Button>
                </div>
            </div>}
            {tool === "door" && <div className="space-y-3 text-sm">
                <p className="text-muted-foreground">Setze Start und Ende mit zwei Klicks. Rechtsklick verwirft
                    den Startpunkt; Strg deaktiviert das Einrasten.</p>
                <p>{state.doorDraft ? "Startpunkt gesetzt" : "Noch kein Startpunkt"}</p>
                <Button type="button" size="icon-sm" variant="outline" disabled={!state.doorDraft}
                        title="Türentwurf verwerfen" onClick={commands.clearDoorDraft}><Trash2/></Button>
            </div>}
            {tool === "transition" && <div className="space-y-3 text-sm">
                <p className="text-muted-foreground">Setze mindestens drei Eckpunkte. Doppelklick oder Klick auf
                    den Startpunkt schließt die Fläche. Rechtsklick verwirft den letzten Punkt; Strg deaktiviert
                    das Einrasten.</p>
                <p>{state.transitionDraft.length} Eckpunkte</p>
                {state.transitionDraft.length >= 3 && !validTransitionPolygon(state.transitionDraft) &&
                    <p className="text-destructive">Die Fläche darf sich nicht selbst schneiden oder auf eine Linie zusammenfallen.</p>}
                <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={!validTransitionPolygon(state.transitionDraft)}
                        onClick={onFinishTransition}><Check/> Fläche abschließen</Button>
                    <Button type="button" size="icon-sm" variant="outline"
                        disabled={state.transitionDraft.length === 0} title="Entwurf verwerfen"
                        onClick={commands.clearTransitionDraft}><Trash2/></Button>
                </div>
            </div>}
            {tool === "select" && (lights.selected ? <LightInspector light={lights.selected}
                onChange={changes => lights.update(lights.selected!.id, changes)}
                onPreview={changes => lights.previewChange(lights.selected!.id, changes)}
                onCommitPreview={changes => lights.commitPreview(lights.selected!.id, changes)}
                onDelete={() => lights.remove(lights.selected!.id)}/> : selectedWall ? <div className="space-y-3 text-sm">
                <p>{selectedWall.points.length} Wandpunkte</p>
                <Button type="button" size="sm" variant="destructive" onClick={commands.deleteSelection}>
                    <Trash2/>Wand löschen
                </Button>
            </div> : selectedDoor ? <DoorInspector key={selectedDoor.id}
                door={selectedDoor} onChange={changes => commands.updateDoor(selectedDoor.id, changes)}
                onDelete={commands.deleteSelection}/> : selectedTransition ?
                <TransitionInspector key={selectedTransition.id} transition={selectedTransition}
                    sourceLevelId={sourceLevelId} sourceGeometry={document} levels={levels}
                    onChange={changes => commands.updateTransition(selectedTransition.id, changes)}
                    onDelete={commands.deleteSelection}/> :
                <p className="text-sm text-muted-foreground">Wähle eine Wand, Tür, ein Licht, eine Übergangsfläche oder Annotation.</p>)}
        </>}
        {tool === "annotation" && <div className="space-y-3 text-sm">
            <label className="block space-y-1">Art des neuen Markers
                <select value={annotations.newKind}
                        onChange={event => annotations.setNewKind(event.target.value as typeof annotations.newKind)}
                        className="w-full rounded-md border border-input bg-surface p-2">
                    {annotationKinds.map(kind => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
                </select>
            </label>
            <p className="text-muted-foreground">Klicke auf die Karte, um einen Marker zu setzen.</p>
            <div className="space-y-1 border-t pt-3">
                <h3 className="font-semibold">Marker ({annotations.annotations.length})</h3>
                {annotations.annotations.map(annotation => <Button key={annotation.id} type="button" size="sm"
                    variant={annotations.selectedId === annotation.id ? "secondary" : "ghost"}
                    className="h-auto w-full justify-start truncate text-left"
                    onClick={() => annotations.focus(annotation)}>
                    <span className="truncate">{annotation.label ? `${annotation.label} · ` : ""}{annotation.title}</span>
                </Button>)}
            </div>
        </div>}
        {tool === "light" && <div className="space-y-3 text-sm">
            <DirectionalLightInspector light={levelLighting.light} onChange={levelLighting.commit}
                onPreview={levelLighting.preview} onCommit={levelLighting.commit}/>
            <h3 className="border-t pt-3 font-medium">Punktlichter</h3>
            <label className="block space-y-1">Preset für neue Lichter
                <select value={lights.newPreset}
                    onChange={event => lights.setNewPreset(event.target.value as typeof lights.newPreset)}
                    className="w-full rounded-md border border-input bg-surface p-2">
                    {Object.keys(LIGHT_PRESETS).map(preset =>
                        <option key={preset} value={preset}>
                            {LIGHT_PRESET_LABELS[preset as keyof typeof LIGHT_PRESETS]}
                        </option>)}
                </select>
            </label>
            <p className="text-muted-foreground">Klicke auf die Karte, um ein Licht zu setzen. Anschließend kannst du seine Werte bearbeiten.</p>
        </div>}
    </>;
}
